import { HOOK_LABEL, SKILL_RARITIES } from "@shared/game";
import { useQuery } from "@tanstack/react-query";
import { Link, createFileRoute } from "@tanstack/react-router";
import { AnnotationEditor } from "../components/annotation-editor";
import { ElementChip, Implemented, RarityText } from "../components/chips";
import { LoadError, Loading, PageHeader } from "../components/page-state";
import { fillDesc, pct } from "../lib/format";
import { gameTableQuery, skillQuery } from "../lib/queries";

export const Route = createFileRoute("/skills/$id")({ component: SkillPage });

function SkillPage() {
	const { id } = Route.useParams();
	const skill = useQuery(skillQuery(id));
	const meta = useQuery(gameTableQuery("skills"));

	if (skill.isPending) return <Loading what="Loading skill" />;
	if (skill.isError) return <LoadError error={skill.error} retry={() => void skill.refetch()} />;

	const s = skill.data;
	const levelMult = (meta.data?.data as { level_multiplier?: Record<string, number> } | undefined)?.level_multiplier ?? { "1": 1, "2": 1.5, "3": 2 };
	const levels = Array.from({ length: s.max_level }, (_, i) => String(i + 1));
	const params = Object.entries(s.params);

	return (
		<div className="space-y-8">
			<PageHeader eyebrow={<Link to="/skills">Skills and elements</Link>} title={s.name}>
				<ElementChip element={s.element} />
				<Implemented value={s.implemented} />
			</PageHeader>

			<section className="grid gap-6 lg:grid-cols-[1fr_1fr]">
				<div className="panel p-5">
					<h2 className="mb-3 text-base">Card text</h2>
					<p className="text-lg" style={{ color: "var(--color-ink-50)" }}>
						{fillDesc(s.desc, s.value_by_rarity.common)}
					</p>
					<dl className="mt-4 grid grid-cols-[8rem_1fr] gap-y-1.5 text-sm">
						<dt style={{ color: "var(--color-ink-300)" }}>Trigger</dt>
						<dd>{HOOK_LABEL[s.hook]}</dd>
						<dt style={{ color: "var(--color-ink-300)" }}>Effect type</dt>
						<dd className="font-mono text-xs">{s.effect_type}</dd>
						{params.map(([k, v]) => (
							<Item key={k} k={k} v={v} />
						))}
						<dt style={{ color: "var(--color-ink-300)" }}>id</dt>
						<dd className="font-mono text-xs">{s.id}</dd>
					</dl>
					{!s.implemented && (
						<p className="mt-4 text-xs" style={{ color: "var(--color-warn)" }}>
							The game has no handler for this effect yet, so the card is never offered.
						</p>
					)}
				</div>

				<div className="panel p-5">
					<h2 className="mb-1 text-base">Value by rarity and level</h2>
					<p className="mb-3 text-xs" style={{ color: "var(--color-ink-400)" }}>
						{s.max_level > 1 ? "Level multiplies the rarity value; chances cap at 100 %." : "Legendary and Mythic skills stay at level 1."}
					</p>
					<table className="table">
						<thead>
							<tr>
								<th>Rarity</th>
								{levels.map((l) => (
									<th key={l} className="num">
										Lv {l}
									</th>
								))}
							</tr>
						</thead>
						<tbody>
							{SKILL_RARITIES.map((r) => (
								<tr key={r}>
									<td>
										<RarityText rarity={r} />
									</td>
									{levels.map((l) => (
										<td key={l} className="num">
											{pct(Math.min(s.value_by_rarity[r] * (levelMult[l] ?? 1), s.effect_type.includes("chance") || s.params["mode"] === "chance" ? 1 : Infinity))}
										</td>
									))}
								</tr>
							))}
						</tbody>
					</table>
				</div>
			</section>

			<AnnotationEditor type="skill" id={s.id} annotation={s.annotation} />
		</div>
	);
}

function Item({ k, v }: { k: string; v: unknown }) {
	return (
		<>
			<dt className="font-mono text-xs" style={{ color: "var(--color-ink-300)" }}>
				{k}
			</dt>
			<dd className="font-mono text-xs">{typeof v === "string" ? v : JSON.stringify(v)}</dd>
		</>
	);
}
